import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import BrandLockup from '@/components/brand/BrandLockup';
import { buildHeaderRoute } from '@/lib/flightRoute';

/**
 * Unified branded A4 layout for every voucher type.
 * Children render inside the body — the frame + header + footer are handled here.
 *
 * The OUTER hero is non-editable by design. Inline-editing (pencil affordance)
 * for activityName / date / venue lives on the INNER voucher-type body
 * components so the editable surface is unambiguous and the chrome stays
 * visually quiet.
 *
 * Props:
 *   voucher: {voucherId, type, status, guestName, destination, ...}
 *   company: {companyName, logoUrl, tagline, primaryColor, accentColor, phone, email, address}
 *   qrValue: string url embedded in the QR code
 *   children: voucher-specific body content
 *   forPdf: when true, outer wrapper matches A4 portrait exactly
 */
export default function BrandedVoucherLayout({
  voucher,
  company,
  qrValue,
  children,
  forPdf = false,
  hideFooterNote = false,
}) {
  const primary = company?.primaryColor || '#34D0CB';
  const accent = company?.accentColor || '#595A5B';

  const typeLabel = {
    flight: 'Flight E-Ticket',
    hotel: 'Hotel Voucher',
    activity: 'Activity Voucher',
    transfer: 'Transfer Voucher',
    itinerary: 'Travel Itinerary',
  }[voucher?.type] || 'Voucher';

  const status = (voucher?.status || 'draft').toLowerCase();
  const statusStyle = {
    draft:     { bg: '#fef3c7', color: '#92400e' },
    confirmed: { bg: '#d1fae5', color: '#065f46' },
    issued:    { bg: '#dbeafe', color: '#1e40af' },
    void:      { bg: '#fee2e2', color: '#991b1b' },
    // Legacy fallbacks
    pending:   { bg: '#FEF3C7', color: '#92400E' },
    cancelled: { bg: '#FEE2E2', color: '#991B1B' },
  }[status] || { bg: primary, color: '#FFFFFF' };

  // Hero title — the LARGE 30 px heading. Shows the activity / hotel /
  // flight subject of the voucher, NOT the guest name. Guest name appears
  // only in the Lead Guest card lower down.
  const heroTitle = (() => {
    const a = voucher?.activity || {};
    const h = voucher?.hotel || {};
    const f = voucher?.flight || {};
    const t = voucher?.transfer || {};
    const it = voucher?.itinerary || {};
    if (voucher?.type === 'activity') {
      return a.activityName || a.name || a.title || a.tourName || 'Activity Voucher';
    }
    if (voucher?.type === 'hotel') {
      return h.hotelName || h.propertyName || h.name || 'Hotel Voucher';
    }
    if (voucher?.type === 'flight') {
      // Prefer the computed origin→destination route string (e.g.
      // "PNQ – CCU via DEL"). Falls through to the legacy `f.from → f.to`
      // string for legacy flat-schema vouchers that have no segments[].
      const richRoute = buildHeaderRoute(f.segments);
      if (richRoute) return richRoute;
      const route = [f.from, f.to].filter(Boolean).join(' → ');
      const fno = [f.airline, f.flightNumber].filter(Boolean).join(' ');
      return route || fno || 'Flight E-Ticket';
    }
    if (voucher?.type === 'transfer') {
      return t.title || t.serviceName || 'Transfer Voucher';
    }
    if (voucher?.type === 'itinerary') {
      return it.title || voucher?.destination || 'Travel Itinerary';
    }
    return typeLabel;
  })();

  // Subtitle line under the hero title. For flight vouchers we show the
  // primary passenger's name (never the airport name — airport belongs in
  // the per-segment detail block). All other voucher types keep their
  // destination string.
  const heroSubtitle = (() => {
    if (voucher?.type === 'flight') {
      return voucher?.flight?.passengers?.[0]?.fullName
        || voucher?.guestName
        || '';
    }
    return voucher?.destination || '';
  })();


  // Global standard PDF padding (applies to all voucher types — Hotel,
  // Flight, Activity, Transfer, Itinerary). These values match the A4
  // industry-standard for travel documents: 16 mm side + 14 mm top/bottom.
  // 16 mm @ 96dpi ≈ 60 px, 14 mm ≈ 53 px. We use 40/32 here because the
  // outer page also has a small margin in the html2pdf options.
  const PAD_X = '40px';   // ~10.5 mm
  const PAD_Y_TOP = '32px';   // ~8.5 mm
  const PAD_Y_BOTTOM = '32px';

  // PDF layout — never use absolute positioning for the footer because
  // html2pdf splits the document into A4 pages and `position:absolute`
  // makes the footer overlap body content on the LAST page (the user's
  // "Footer Overlaps content" bug). Instead, footer sits in normal block
  // flow at the END of the content and lands cleanly on the last page.
  //
  // Browser preview (Phase 15): the wrapper fills its container width
  // 100%. No maxWidth cap so content stretches edge-to-edge as the user
  // requested. PDF rendering path is untouched — Playwright still gets a
  // 210 mm canvas with the 8 mm page margins set in `pdf_service.py`.
  const frameStyle = forPdf
    ? { width: '210mm', minHeight: '297mm' }
    : { width: '100%', minHeight: '820px' };

  return (
    <div
      className="bg-white mx-auto"
      style={{
        ...frameStyle,
        fontFamily: "'Inter', 'Manrope', system-ui, sans-serif",
        color: '#111827',
        display: 'block',
        position: 'relative',
      }}
    >
      {/* Brand stripe at top */}
      <div style={{ height: '6px', background: primary }} />

      {/* Header: brand lockup left, company contact right.
          Explicit block flow + table-like layout so html2canvas renders
          the same in browser and PDF (no flex utility-class drift). */}
      <div
        className="pdf-no-break"
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          padding: `${PAD_Y_TOP} ${PAD_X} 24px ${PAD_X}`,
          pageBreakInside: 'avoid',
        }}
      >
        <div style={{ flex: '0 0 auto' }}>
          <BrandLockup company={company} size={forPdf ? 'lg' : 'md'} forPdf={forPdf} />
        </div>
        <div style={{ textAlign: 'right', fontSize: '10px', lineHeight: '1.5', color: accent, maxWidth: '240px' }}>
          {company?.address && <div>{company.address}</div>}
          {company?.website && <div>{company.website}</div>}
          {company?.email && <div>{company.email}</div>}
          {company?.phone && <div>{company.phone}</div>}
          {company?.gstNumber && <div>GSTIN: {company.gstNumber}</div>}
        </div>
      </div>

      {/* Hero block: type tag + guest + status */}
      <div
        className="pdf-no-break"
        style={{
          borderTop: '1px solid #E5E7EB',
          borderBottom: '1px solid #E5E7EB',
          background: '#FAFAFA',
          padding: `24px ${PAD_X}`,
          pageBreakInside: 'avoid',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: '24px',
          }}
        >
          <div style={{ flex: '1 1 auto', minWidth: 0 }}>
            <div
              className="uppercase font-semibold"
              style={{ fontSize: '10px', letterSpacing: '0.22em', marginBottom: '8px', color: primary }}
            >
              {typeLabel}
            </div>
            <div
              className="font-bold"
              style={{ fontSize: '30px', letterSpacing: '-0.02em', lineHeight: '1.1', color: '#111827', wordBreak: 'break-word' }}
              data-testid="voucher-hero-title"
            >
              {heroTitle}
            </div>
            {heroSubtitle && (
              <div
                style={{ fontSize: '14px', marginTop: '4px', color: accent }}
                data-testid="voucher-hero-subtitle"
              >
                {heroSubtitle}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '12px', flexShrink: 0 }}>
            <span
              className="uppercase font-bold"
              style={{
                display: 'inline-block',
                padding: '6px 12px',
                fontSize: '10px',
                borderRadius: '9999px',
                background: statusStyle.bg,
                color: statusStyle.color,
                letterSpacing: '0.15em',
              }}
            >
              {status}
            </span>
            <div className="font-mono" style={{ fontSize: '11px', color: accent, letterSpacing: '0.08em' }}>
              {voucher?.trvRef || voucher?.voucherId}
            </div>
          </div>
        </div>
      </div>

      {/* Body — uses the same horizontal padding as header/footer for
          global visual rhythm across all voucher types. */}
      <div style={{ padding: `32px ${PAD_X}` }}>{children}</div>

      {/* QR + Footer — NORMAL BLOCK FLOW (NOT absolute).
          Previously this used `position: absolute; bottom: 0` which caused
          the footer to overlap content on the last page in multi-page PDFs
          (the "Footer Overlaps content" bug). Now it sits naturally after
          the body and lands cleanly on the final page. */}
      <div className="pdf-no-break" style={{ pageBreakInside: 'avoid', marginTop: 'auto' }}>
        {!hideFooterNote && (
        <div
          style={{
            padding: `20px ${PAD_X} ${PAD_Y_BOTTOM} ${PAD_X}`,
            borderTop: '1px solid #E5E7EB',
            background: '#FAFAFA',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '24px',
          }}
        >
          <div style={{ flex: '1 1 auto', fontSize: '10px', lineHeight: '1.55', color: accent, minWidth: 0 }}>
            <div className="font-semibold" style={{ marginBottom: '4px', color: '#111827' }}>
              Need help with your booking?
            </div>
            <div style={{ wordBreak: 'break-word' }}>
              Contact {company?.companyName || 'Travcations'}
              {company?.phone ? ` at ${company.phone}` : ''}
              {company?.email ? ` · ${company.email}` : ''}.
            </div>
            <div className="font-mono" style={{ marginTop: '6px' }}>TRV Ref: {voucher?.trvRef || voucher?.voucherId}</div>
            {voucher?.displayTripId && (
              <div className="font-mono" style={{ marginTop: '2px' }} data-testid="voucher-trip-ref">Trip Ref: {voucher.displayTripId}</div>
            )}
          </div>
          {/* QR Verify widget removed globally per Phase 9 refinement.
              Kept commented out for emergency rollback only.

          {qrValue && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
              <div style={{ padding: '6px', background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '6px' }}>
                <QRCodeSVG value={qrValue} size={72} level="M" fgColor="#111827" bgColor="#FFFFFF" />
              </div>
              <div
                className="font-mono uppercase"
                style={{ fontSize: '8px', letterSpacing: '0.18em', color: accent }}
              >
                Verify
              </div>
            </div>
          )}
          */}
        </div>
        )}
        <div style={{ height: '4px', background: primary }} />
      </div>
    </div>
  );
}

/* ---------- Shared sub-components for voucher bodies ---------- */

export function SectionTitle({ children, primary }) {
  return (
    <div
      className="text-[11px] font-bold uppercase mb-4"
      style={{ letterSpacing: '0.2em', color: primary || '#34D0CB' }}
    >
      {children}
    </div>
  );
}

export function InfoCard({ label, value, primary, accent, wide = false, mono = false }) {
  return (
    <div
      className={wide ? 'col-span-2' : ''}
      style={{ padding: '14px 16px', background: '#F8FAFC', borderRadius: '10px', border: '1px solid #E5E7EB' }}
    >
      <div
        className="text-[9px] font-semibold uppercase mb-1.5"
        style={{ letterSpacing: '0.18em', color: accent || '#595A5B' }}
      >
        {label}
      </div>
      <div
        className={mono ? 'font-mono text-sm font-bold' : 'text-sm font-bold'}
        style={{ color: '#111827', wordBreak: 'break-word' }}
      >
        {value || '—'}
      </div>
    </div>
  );
}
